import React, { useState, useEffect } from 'react'
import { platformAPI, subscriptionTiersAPI } from '../services/api'
import { 
  X, 
  Building2, 
  Mail, 
  Tag, 
  Palette, 
  Globe,
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react'

export const CreateTenantModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    owner_email: '',
    billing_email: '',
    domain: '',
    subscription_tier: 'starter',
    branding: {
      business_name: '',
      primary_color: '#8B5CF6',
      secondary_color: '#1F2937',
      font_family: 'Inter',
      logo_url: '',
      tagline: '',
      welcome_message: '',
      support_email: '',
      support_phone: ''
    }
  })
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [validationErrors, setValidationErrors] = useState({})
  const [subscriptionTiers, setSubscriptionTiers] = useState([])
  const [tiersLoading, setTiersLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let canceled = false
    const loadTiers = async () => {
      setTiersLoading(true)
      try {
        const res = await subscriptionTiersAPI.getAll()
        const tiers = res?.data?.data || []
        if (canceled) return
        setSubscriptionTiers(tiers)
        if (tiers.length > 0 && !tiers.find(t => t.name === formData.subscription_tier)) {
          setFormData(prev => ({ ...prev, subscription_tier: tiers[0].name }))
        }
      } catch (err) {
        console.error('Failed to load subscription tiers:', err)
      } finally {
        if (!canceled) setTiersLoading(false)
      }
    }
    loadTiers()
    return () => { canceled = true }
  }, [isOpen])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleBrandingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      branding: {
        ...prev.branding,
        [field]: value
      }
    }))
  }

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50)
  }

  const handleNameChange = (name) => {
    handleInputChange('name', name)
    
    // Auto-generate slug if it's empty or matches the previous auto-generated one
    if (!formData.slug || formData.slug === generateSlug(formData.name)) {
      handleInputChange('slug', generateSlug(name))
    }
    
    // Auto-populate business_name if it's empty
    if (!formData.branding.business_name) {
      handleBrandingChange('business_name', name)
    }
  }

  const validateForm = () => {
    const errors = {}
    
    // Required fields
    if (!formData.name.trim()) {
      errors.name = 'Informe o nome do estúdio'
    }
    
    if (!formData.slug.trim()) {
      errors.slug = 'Informe o slug'
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      errors.slug = 'O slug só pode conter letras minúsculas, números e hífens'
    } else if (formData.slug.length < 3) {
      errors.slug = 'O slug deve ter pelo menos 3 caracteres'
    }
    
    if (!formData.billing_email.trim()) {
      errors.billing_email = 'Informe o e-mail de cobrança'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billing_email)) {
      errors.billing_email = 'Informe um endereço de e-mail válido'
    }

    if (!formData.owner_email.trim()) {
      errors.owner_email = 'Informe o e-mail do proprietário'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) {
      errors.owner_email = 'Informe um endereço de e-mail válido'
    }

    // Validate domain if provided
    if (formData.domain && !/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.([a-zA-Z]{2,})+$/.test(formData.domain)) {
      errors.domain = 'Informe um domínio válido (ex.: exemplo.com.br)'
    }

    // Validate support email if provided
    if (formData.branding.support_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.branding.support_email)) {
      errors.support_email = 'Informe um e-mail de suporte válido'
    }

    // Validate support phone if provided
    if (formData.branding.support_phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.branding.support_phone.replace(/[\s\-\(\)]/g, ''))) {
      errors.support_phone = 'Informe um telefone válido'
    }

    // Validate logo URL if provided
    if (formData.branding.logo_url && !/^https?:\/\/.+/.test(formData.branding.logo_url)) {
      errors.logo_url = 'Informe uma URL válida (http:// ou https://)'
    }

    // Validate colors
    if (!/^#[0-9A-Fa-f]{6}$/.test(formData.branding.primary_color)) {
      errors.primary_color = 'Informe uma cor hexadecimal válida (ex.: #8B5CF6)'
    }
    
    if (!/^#[0-9A-Fa-f]{6}$/.test(formData.branding.secondary_color)) {
      errors.secondary_color = 'Informe uma cor hexadecimal válida (ex.: #1F2937)'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLoading(true)
    setError('')



    try {
      // Clean the payload - remove empty strings and unnecessary fields
      const cleanPayload = {
        name: formData.name,
        slug: formData.slug,
        owner_email: formData.owner_email,
        billing_email: formData.billing_email,
        subscription_tier: formData.subscription_tier
      }

      // Add optional fields only if they have values
      if (formData.domain && formData.domain.trim()) {
        cleanPayload.domain = formData.domain.trim()
      }

      // Clean branding object - only include fields with actual values
      const branding = {}
      if (formData.branding.business_name && formData.branding.business_name.trim()) {
        branding.business_name = formData.branding.business_name.trim()
      }
      if (formData.branding.primary_color && formData.branding.primary_color !== '#8B5CF6') {
        branding.primary_color = formData.branding.primary_color
      }
      if (formData.branding.secondary_color && formData.branding.secondary_color !== '#1F2937') {
        branding.secondary_color = formData.branding.secondary_color
      }
      if (formData.branding.font_family && formData.branding.font_family !== 'Inter') {
        branding.font_family = formData.branding.font_family
      }
      if (formData.branding.logo_url && formData.branding.logo_url.trim()) {
        branding.logo_url = formData.branding.logo_url.trim()
      }
      if (formData.branding.tagline && formData.branding.tagline.trim()) {
        branding.tagline = formData.branding.tagline.trim()
      }
      if (formData.branding.welcome_message && formData.branding.welcome_message.trim()) {
        branding.welcome_message = formData.branding.welcome_message.trim()
      }
      if (formData.branding.support_email && formData.branding.support_email.trim()) {
        branding.support_email = formData.branding.support_email.trim()
      }
      if (formData.branding.support_phone && formData.branding.support_phone.trim()) {
        branding.support_phone = formData.branding.support_phone.trim()
      }

      // Only add branding if there are actual branding fields
      if (Object.keys(branding).length > 0) {
        cleanPayload.branding = branding
      }



      const response = await platformAPI.createTenant(cleanPayload)
      
      // Success
      onSuccess(response.data)
      onClose()
      
      // Reset form
      setFormData({
        name: '',
        slug: '',
        owner_email: '',
        billing_email: '',
        domain: '',
        subscription_tier: 'starter',
        branding: {
          business_name: '',
          primary_color: '#8B5CF6',
          secondary_color: '#1F2937',
          font_family: 'Inter',
          logo_url: '',
          tagline: '',
          welcome_message: '',
          support_email: '',
          support_phone: ''
        }
      })
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Falha na autenticação. Saia e entre novamente.')
      } else {
        setError(err.response?.data?.error || 'Falha ao criar o estúdio. Tente novamente.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
      setError('')
      setValidationErrors({})
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-admin-200">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg mr-3">
              <Building2 className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-admin-900">Criar novo estúdio</h2>
              <p className="text-sm text-admin-600">Configure um novo estúdio cliente</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            aria-label="Fechar"
            className="p-2 text-admin-400 hover:text-admin-600 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}



          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-admin-900 flex items-center">
              <Building2 className="h-5 w-5 mr-2" />
              Informações básicas
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Nome do estúdio *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={`input ${validationErrors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="Studio Maria Silva"
                  maxLength={255}
                  disabled={loading}
                />
                {validationErrors.name && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Slug *
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  className={`input font-mono text-sm ${validationErrors.slug ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="studio-maria-silva"
                  maxLength={50}
                  disabled={loading}
                />
                {validationErrors.slug && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.slug}</p>
                )}
                <p className="text-xs text-admin-500 mt-1">Usado nas URLs (apenas minúsculas, números e hífens)</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  E-mail do proprietário *
                </label>
                <div className="relative">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-admin-400" />
                  <input
                    type="email"
                    value={formData.owner_email}
                    onChange={(e) => handleInputChange('owner_email', e.target.value)}
                    className={`input pl-10 ${validationErrors.owner_email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="maria@studiomariasilva.com.br"
                    disabled={loading}
                  />
                </div>
                {validationErrors.owner_email && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.owner_email}</p>
                )}
                <p className="text-xs text-admin-500 mt-1">E-mail de acesso do administrador do estúdio</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  E-mail de cobrança *
                </label>
                <div className="relative">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-admin-400" />
                  <input
                    type="email"
                    value={formData.billing_email}
                    onChange={(e) => handleInputChange('billing_email', e.target.value)}
                    className={`input pl-10 ${validationErrors.billing_email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="financeiro@studiomariasilva.com.br"
                    disabled={loading}
                  />
                </div>
                {validationErrors.billing_email && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.billing_email}</p>
                )}
                <p className="text-xs text-admin-500 mt-1">Para faturas e pagamentos</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-admin-700 mb-1">
                Domínio personalizado
              </label>
              <input
                type="text"
                value={formData.domain}
                onChange={(e) => handleInputChange('domain', e.target.value)}
                className={`input ${validationErrors.domain ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                placeholder="exemplo.com.br"
                disabled={loading}
              />
              {validationErrors.domain && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.domain}</p>
              )}
              <p className="text-xs text-admin-500 mt-1">Domínio personalizado opcional para este estúdio</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-admin-700 mb-1">
                Plano de assinatura *
              </label>
              {tiersLoading ? (
                <div className="flex items-center gap-2 text-sm text-admin-500 py-3">
                  <Loader className="h-4 w-4 animate-spin" />
                  Carregando planos...
                </div>
              ) : subscriptionTiers.length === 0 ? (
                <p className="text-sm text-admin-500 py-3">Nenhum plano de assinatura disponível.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {subscriptionTiers.map((tier) => {
                    const limits = [
                      tier.max_clients === -1 ? '∞ clientes' : `${tier.max_clients} clientes`,
                      tier.max_products === -1 ? '∞ produtos' : `${tier.max_products} produtos`,
                      tier.max_users === -1 ? '∞ usuários' : `${tier.max_users} usuários`,
                    ]
                    const features = [
                      tier.whatsapp_enabled && 'WhatsApp',
                      tier.analytics_enabled && 'Análises',
                      tier.api_access && 'API',
                    ].filter(Boolean)

                    return (
                      <label key={tier.name} className="relative">
                        <input
                          type="radio"
                          value={tier.name}
                          checked={formData.subscription_tier === tier.name}
                          onChange={(e) => handleInputChange('subscription_tier', e.target.value)}
                          className="sr-only"
                          disabled={loading}
                        />
                        <div
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            formData.subscription_tier === tier.name
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-admin-200 hover:border-admin-300'
                          }`}
                          role="radio"
                          aria-checked={formData.subscription_tier === tier.name}
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') handleInputChange('subscription_tier', tier.name) }}
                        >
                          <div className="font-medium text-sm text-admin-900">{tier.display_name}</div>
                          <div className="text-xs text-admin-500 mt-1">{limits.join(' · ')}</div>
                          {features.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {features.map((f) => (
                                <span key={f} className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 font-medium">{f}</span>
                              ))}
                            </div>
                          )}
                          {tier.price_monthly > 0 && (
                            <div className="text-xs text-admin-600 mt-1">${tier.price_monthly}/mês</div>
                          )}
                        </div>
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Branding */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-admin-900 flex items-center">
              <Palette className="h-5 w-5 mr-2" />
              Identidade visual e personalização
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Nome comercial
                </label>
                <input
                  type="text"
                  value={formData.branding.business_name}
                  onChange={(e) => handleBrandingChange('business_name', e.target.value)}
                  className="input"
                  placeholder="Studio Maria Silva"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  E-mail de suporte
                </label>
                <div className="relative">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-admin-400" />
                  <input
                    type="email"
                    value={formData.branding.support_email}
                    onChange={(e) => handleBrandingChange('support_email', e.target.value)}
                    className={`input pl-10 ${validationErrors.support_email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="contato@studiomariasilva.com.br"
                    disabled={loading}
                  />
                </div>
                {validationErrors.support_email && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.support_email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Telefone de suporte
                </label>
                <input
                  type="tel"
                  value={formData.branding.support_phone}
                  onChange={(e) => handleBrandingChange('support_phone', e.target.value)}
                  className={`input ${validationErrors.support_phone ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="+55 (11) 91234-5678"
                  disabled={loading}
                />
                {validationErrors.support_phone && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.support_phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  URL do logo
                </label>
                <input
                  type="url"
                  value={formData.branding.logo_url}
                  onChange={(e) => handleBrandingChange('logo_url', e.target.value)}
                  className={`input ${validationErrors.logo_url ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="https://exemplo.com.br/logo.png"
                  disabled={loading}
                />
                {validationErrors.logo_url && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.logo_url}</p>
                )}
                <p className="text-xs text-admin-500 mt-1">URL da imagem do logo do estúdio</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Cor primária
                </label>
                <div className="flex">
                  <input
                    type="color"
                    value={formData.branding.primary_color}
                    onChange={(e) => handleBrandingChange('primary_color', e.target.value)}
                    className="h-10 w-16 rounded-l-lg border border-admin-300"
                    disabled={loading}
                  />
                  <input
                    type="text"
                    value={formData.branding.primary_color}
                    onChange={(e) => handleBrandingChange('primary_color', e.target.value)}
                    className={`input rounded-l-none flex-1 font-mono text-sm ${validationErrors.primary_color ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="#8B5CF6"
                    disabled={loading}
                  />
                </div>
                {validationErrors.primary_color && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.primary_color}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Cor secundária
                </label>
                <div className="flex">
                  <input
                    type="color"
                    value={formData.branding.secondary_color}
                    onChange={(e) => handleBrandingChange('secondary_color', e.target.value)}
                    className="h-10 w-16 rounded-l-lg border border-admin-300"
                    disabled={loading}
                  />
                  <input
                    type="text"
                    value={formData.branding.secondary_color}
                    onChange={(e) => handleBrandingChange('secondary_color', e.target.value)}
                    className={`input rounded-l-none flex-1 font-mono text-sm ${validationErrors.secondary_color ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="#1F2937"
                    disabled={loading}
                  />
                </div>
                {validationErrors.secondary_color && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.secondary_color}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-admin-700 mb-1">
                Slogan
              </label>
              <input
                type="text"
                value={formData.branding.tagline}
                onChange={(e) => handleBrandingChange('tagline', e.target.value)}
                className="input"
                placeholder="Seu estilo, sua essência"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-admin-700 mb-1">
                Mensagem de boas-vindas
              </label>
              <textarea
                value={formData.branding.welcome_message}
                onChange={(e) => handleBrandingChange('welcome_message', e.target.value)}
                className="input"
                placeholder="Boas-vindas à nossa experiência de atendimento personalizado!"
                rows={3}
                disabled={loading}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-admin-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="btn btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? (
                <div className="flex items-center">
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Criando...
                </div>
              ) : (
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Criar estúdio
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
} 